import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { socialApi, employeesApi } from '@/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import type { SocialConversationResponse, SocialMessageResponse } from '@/types/social';
import type { EmployeeResponse } from '@/types/employee';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { SocialCrmActionBar } from './SocialCrmActionBar';
import { useSocialAccounts } from '../hooks/useSocialAccounts';

const SAVED_REPLIES_KEY = 'social.savedReplies';

function loadSavedReplies(): string[] {
  try {
    const raw = localStorage.getItem(SAVED_REPLIES_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function persistSavedReplies(replies: string[]) {
  localStorage.setItem(SAVED_REPLIES_KEY, JSON.stringify(replies.slice(0, 20)));
}

interface SocialInboxWorkspaceProps {
  platformFilter?: string;
  title?: string;
  showSimulate?: boolean;
}

export function SocialInboxWorkspace({
  platformFilter,
  title = 'Conversation',
  showSimulate = true,
}: SocialInboxWorkspaceProps) {
  const { connected, accounts } = useSocialAccounts();
  const conversationsPagination = usePagination(20);
  const messagesPagination = usePagination(30);
  const [conversations, setConversations] = useState<SocialConversationResponse[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [messages, setMessages] = useState<SocialMessageResponse[]>([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [assigneeId, setAssigneeId] = useState<number | ''>('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [internalNote, setInternalNote] = useState('');
  const [savedNotes, setSavedNotes] = useState<{ id: number; noteText: string; createdAt?: string }[]>([]);
  const [savingNote, setSavingNote] = useState(false);
  const [orgTags, setOrgTags] = useState<{ id: number; name: string }[]>([]);
  const [savedReplies, setSavedReplies] = useState<string[]>(() => loadSavedReplies());
  const [simulateAccountId, setSimulateAccountId] = useState<number | ''>('');
  const [simulateMessage, setSimulateMessage] = useState('Hi, I need help with my order');

  const filteredAccounts = useMemo(() => {
    if (!platformFilter) return connected;
    return connected.filter((a) => a.platform.toUpperCase() === platformFilter.toUpperCase());
  }, [connected, platformFilter]);

  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await socialApi.listConversations({
        page: conversationsPagination.page,
        size: conversationsPagination.size,
      });
      let rows = res.content;
      if (platformFilter) {
        rows = rows.filter((c) => c.platform.toUpperCase() === platformFilter.toUpperCase());
      }
      setConversations(rows);
      conversationsPagination.applyPageResponse(res);
      if (platformFilter) conversationsPagination.applyClientCount(rows.length);
      setSelectedId((prev) => {
        if (prev != null && rows.some((c) => c.id === prev)) return prev;
        return rows[0]?.id ?? null;
      });
    } catch (err) {
      showApiError(err, 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, [conversationsPagination, platformFilter]);

  const loadMessages = useCallback(
    async (conversationId: number) => {
      setMessagesLoading(true);
      try {
        const res = await socialApi.listMessages(conversationId, {
          page: messagesPagination.page,
          size: messagesPagination.size,
        });
        setMessages(res.content);
        messagesPagination.applyPageResponse(res);
        socialApi.markConversationRead(conversationId).catch(() => undefined);
      } catch (err) {
        showApiError(err, 'Failed to load messages');
      } finally {
        setMessagesLoading(false);
      }
    },
    [messagesPagination],
  );

  useEffect(() => {
    employeesApi.list({ page: 0, size: 100 }).then((r) => setEmployees(r.content)).catch(() => undefined);
    socialApi.listTags().then(setOrgTags).catch(() => undefined);
  }, []);

  useEffect(() => {
    void loadConversations();
    const timer = setInterval(() => void loadConversations(), 20000);
    return () => clearInterval(timer);
  }, [loadConversations]);

  useEffect(() => {
    if (selectedId != null) void loadMessages(selectedId);
  }, [selectedId, loadMessages]);

  useEffect(() => {
    if (selectedId == null) {
      setSavedNotes([]);
      setInternalNote('');
      return;
    }
    socialApi
      .listInternalNotes('CONVERSATION', selectedId)
      .then(setSavedNotes)
      .catch(() => setSavedNotes([]));
    setInternalNote('');
    setTags([]);
  }, [selectedId]);

  const selected = conversations.find((c) => c.id === selectedId);

  const sendReply = async () => {
    if (!selectedId || !reply.trim()) return;
    try {
      await socialApi.sendMessage(selectedId, { messageContent: reply });
      setReply('');
      await loadMessages(selectedId);
      await loadConversations();
      notify.success('Reply sent');
    } catch (err) {
      showApiError(err, 'Failed to send message');
    }
  };

  const assign = async () => {
    if (!selectedId || assigneeId === '') return;
    try {
      await socialApi.assignConversation(selectedId, Number(assigneeId));
      notify.success('Conversation assigned');
      await loadConversations();
    } catch (err) {
      showApiError(err, 'Assignment failed');
    }
  };

  const escalate = async () => {
    if (!selectedId) return;
    try {
      await socialApi.escalateConversation(selectedId);
      notify.success('Escalated — Desk ticket workflow triggered');
      await loadConversations();
    } catch (err) {
      showApiError(err, 'Escalation failed');
    }
  };

  const addTag = async () => {
    const t = tagInput.trim();
    if (!t || tags.includes(t)) return;
    setTags([...tags, t]);
    setTagInput('');
    if (!orgTags.some((o) => o.name.toLowerCase() === t.toLowerCase())) {
      try {
        const created = await socialApi.createTag(t);
        setOrgTags((prev) => [...prev, created]);
      } catch {
        /* local tag still shown */
      }
    }
  };

  const saveInternalNote = async () => {
    if (!selectedId || !internalNote.trim()) return;
    setSavingNote(true);
    try {
      const note = await socialApi.addInternalNote('CONVERSATION', selectedId, internalNote.trim());
      setSavedNotes((prev) => [{ id: note.id, noteText: note.noteText, createdAt: note.createdAt }, ...prev]);
      setInternalNote('');
      notify.success('Note saved for your team');
    } catch (err) {
      showApiError(err, 'Could not save note');
    } finally {
      setSavingNote(false);
    }
  };

  const saveReplyTemplate = () => {
    const t = reply.trim();
    if (!t) return;
    const next = [t, ...savedReplies.filter((r) => r !== t)].slice(0, 12);
    setSavedReplies(next);
    persistSavedReplies(next);
    notify.success('Saved reply added');
  };

  const simulateInbound = async () => {
    if (!simulateAccountId) {
      notify.error('Select a connected account');
      return;
    }
    try {
      await socialApi.simulateInbound(Number(simulateAccountId), simulateMessage);
      notify.success('Inbound message simulated');
      conversationsPagination.resetPage();
      await loadConversations();
    } catch (err) {
      showApiError(err, 'Simulation failed');
    }
  };

  return (
    <div>
      {showSimulate && filteredAccounts.length > 0 && (
        <div className="card mb-4 grid gap-3 p-4 sm:grid-cols-3">
          <Select
            label="Simulate inbound (demo)"
            value={simulateAccountId === '' ? '' : String(simulateAccountId)}
            onChange={(v) => setSimulateAccountId(v ? Number(v) : '')}
            options={filteredAccounts.map((a) => ({
              value: String(a.id),
              label: `${a.platform} — ${a.accountName}`,
            }))}
          />
          <Input label="Message" value={simulateMessage} onChange={(e) => setSimulateMessage(e.target.value)} />
          <div className="flex items-end">
            <Button onClick={simulateInbound}>Send test message</Button>
          </div>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-12">
        <aside className="card flex max-h-[75vh] flex-col overflow-hidden xl:col-span-3">
          <div className="border-b px-3 py-2 text-sm font-semibold dark:border-slate-700">{title}</div>
          <div className="flex-1 overflow-y-auto p-2">
            {loading && <p className="p-3 text-sm text-slate-500">Loading…</p>}
            {!loading && conversations.length === 0 && (
              <p className="p-3 text-sm text-slate-500">
                No conversations.{' '}
                <Link to="/social/connect?tab=channels" className="text-primary-600 hover:underline">
                  Connect a channel
                </Link>
              </p>
            )}
            {conversations.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`mb-1 w-full rounded-lg p-3 text-left text-sm ${
                  selectedId === c.id
                    ? 'bg-primary-50 dark:bg-primary-900/30'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
                onClick={() => {
                  setSelectedId(c.id);
                  messagesPagination.resetPage();
                }}
              >
                <p className="font-medium">{c.subject ?? `${c.platform} conversation`}</p>
                <p className="text-xs text-slate-500">
                  {c.platform} · {c.unreadCount} unread · SLA {c.conversationStatus}
                </p>
              </button>
            ))}
          </div>
          <TablePagination
            page={conversationsPagination.page}
            totalPages={conversationsPagination.totalPages}
            totalElements={conversationsPagination.totalElements}
            onPageChange={conversationsPagination.setPage}
            loading={loading}
            hasRows={conversations.length > 0}
          />
        </aside>

        <section className="card flex max-h-[75vh] flex-col overflow-hidden xl:col-span-9">
          {selectedId == null ? (
            <p className="p-6 text-sm text-slate-500">Select a conversation</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3 dark:border-slate-700">
                <span className="text-sm font-semibold">{selected?.platform}</span>
                <Select
                  label=""
                  className="min-w-[140px]"
                  value={assigneeId === '' ? '' : String(assigneeId)}
                  onChange={(v) => setAssigneeId(v ? Number(v) : '')}
                  options={[
                    { value: '', label: 'Assign to…' },
                    ...employees.map((e) => ({
                      value: String(e.id),
                      label: `${e.firstName} ${e.lastName}`,
                    })),
                  ]}
                />
                <Button size="sm" variant="secondary" onClick={assign}>
                  Assign
                </Button>
                <Button size="sm" variant="secondary" onClick={escalate}>
                  Escalate
                </Button>
                <span className="ml-auto rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  SLA: respond within 4h
                </span>
              </div>

              {selected && (
                <div className="border-b px-4 py-2 dark:border-slate-700">
                  <SocialCrmActionBar
                    sourceType="conversation"
                    platform={selected.platform}
                    customerId={selected.customerId}
                    compact
                  />
                </div>
              )}

              <div className="flex flex-1 flex-col gap-0 overflow-hidden lg:flex-row">
                <div className="flex flex-1 flex-col overflow-hidden">
                  <div className="flex-1 space-y-3 overflow-y-auto p-4">
                    {messagesLoading && <p className="text-sm text-slate-500">Loading messages…</p>}
                    {messages.map((m) => (
                      <div key={m.id} className="space-y-1">
                        <div
                          className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                            m.direction === 'OUTBOUND'
                              ? 'ml-auto bg-primary-600 text-white'
                              : 'bg-slate-100 dark:bg-slate-800'
                          }`}
                        >
                          <p>{m.messageContent}</p>
                          <p className="mt-1 flex flex-wrap gap-2 text-xs opacity-70">
                            <span>{m.messageStatus}</span>
                            {m.sentimentLabel && <span>· {m.sentimentLabel}</span>}
                          </p>
                        </div>
                        {m.direction === 'INBOUND' && selected && (
                          <SocialCrmActionBar
                            sourceType="message"
                            platform={m.platform}
                            preview={m.messageContent}
                            customerId={selected.customerId}
                            compact
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="border-t p-3 dark:border-slate-700">
                    {savedReplies.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-1">
                        {savedReplies.map((r) => (
                          <button
                            key={r}
                            type="button"
                            className="rounded-full bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800"
                            onClick={() => setReply(r)}
                          >
                            {r.slice(0, 40)}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Input
                        className="min-w-0 flex-1"
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder="Type a reply…"
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && void sendReply()}
                      />
                      <Button onClick={() => void sendReply()}>Send</Button>
                      <Button variant="secondary" onClick={saveReplyTemplate}>
                        Save reply
                      </Button>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      AI Assist: suggest empathetic reply (demo — wire to your AI provider)
                    </p>
                  </div>
                </div>

                <aside className="w-full shrink-0 border-t p-3 dark:border-slate-700 lg:w-56 lg:border-l lg:border-t-0">
                  <p className="mb-2 text-xs font-semibold uppercase text-slate-500">Tags</p>
                  <div className="mb-2 flex flex-wrap gap-1">
                    {tags.map((t) => (
                      <span key={t} className="rounded bg-cyan-50 px-2 py-0.5 text-xs text-cyan-800 dark:bg-cyan-950 dark:text-cyan-200">
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag" />
                    <Button size="sm" variant="secondary" onClick={() => void addTag()}>
                      +
                    </Button>
                  </div>
                  {orgTags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {orgTags.slice(0, 8).map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          className="rounded bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800"
                          onClick={() => !tags.includes(o.name) && setTags([...tags, o.name])}
                        >
                          {o.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="mb-2 mt-4 text-xs font-semibold uppercase text-slate-500">Internal notes</p>
                  {savedNotes.length > 0 && (
                    <ul className="mb-2 max-h-24 space-y-1 overflow-y-auto text-xs text-slate-600 dark:text-slate-400">
                      {savedNotes.map((n) => (
                        <li key={n.id} className="rounded border px-2 py-1 dark:border-slate-700">
                          {n.noteText}
                        </li>
                      ))}
                    </ul>
                  )}
                  <textarea
                    className="input min-h-[72px] w-full text-sm"
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    placeholder="Team-only note (not sent to customer)…"
                  />
                  <Button size="sm" className="mt-2 w-full" variant="secondary" loading={savingNote} onClick={() => void saveInternalNote()}>
                    Save note
                  </Button>
                  <p className="mt-4 text-xs text-slate-500">
                    Customer history:{' '}
                    {selected?.customerId ? (
                      <Link to={`/crm/contacts`} className="text-link">
                        CRM #{selected.customerId}
                      </Link>
                    ) : (
                      'Link after lead conversion'
                    )}
                  </p>
                </aside>
              </div>
            </>
          )}
        </section>
      </div>
      {accounts.length === 0 && !loading && (
        <p className="mt-2 text-xs text-slate-500">No accounts loaded — check channel connections.</p>
      )}
    </div>
  );
}
